import Grid from "@material-ui/core/Grid";
import { CommodityInput } from "components/inputs/CommodityInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { CountryISOCodeInput } from "components/inputs/CountryISOCodeInput";
import { HiddenInput } from "components/inputs/HiddenInput";
import { PlaceSelectInput } from "components/inputs/PlaceSelectInput";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import { ExternalTerminalConnectionStatus } from "entities/ExternalTerminalConnectionStatus";
import React, { useState } from "react";
import {
	ReferenceInput,
	SelectInput,
	TextInput,
	maxLength,
	useDataProvider,
	usePermissions,
	useTranslate,
} from "react-admin";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";

import { formatPhoneNumber } from "../../../components/fields/PhoneField";
import { FormGroupGrid, GridForm, GridInput } from "../../../components/GridForm";
import { NumberInputEx } from "../../../components/inputs";
import { DateInput, TimeInput } from "../../../components/inputs";
import { PhoneInput } from "../../../components/inputs";
import { OrderReferenceInput } from "../../../components/inputs/OrderReferenceInput";
import { OSFCUserSelectInput } from "../../../components/inputs/OSFCUserSelect";
import { PortInput } from "../../../components/inputs/PortInput";
import { SelectTextInput } from "../../../components/inputs/SelectTextInput";
import { SelectUppercaseInput } from "../../../components/inputs/SelectUppercaseInput";
import { SelectDisabledInput } from "../../../components/inputs/SelectUppercaseInput";
import TerminalDatesInput from "../../../components/inputs/TerminalDatesInput";
import { YesNoInput } from "../../../components/inputs/YesNoInput";
import {
	OrderService,
	OrderStatus,
	OrderStatusAllowedFromDraft,
	OrderStatusCustomerEdit,
	OrderStatusCustomerEditOrder,
	OrderStatusEditOrderTerminal,
	OrderStatusTransporterEditOrder_1,
	OrderStatusTransporterEditOrder_2,
	UserRoleByCompanyType,
	etdThirdAssigneeServices,
	perEligibleServices,
} from "../../../entities";
import HideableOrderField from "../../../entities/HideableOrderField";
import { LabelSource } from "../../../entities/LabelSource";
import { useTerminalFieldVisibility } from "../../../hooks/useTerminalFieldVisibility";
import { isStorageModuleEnabled } from "../../../utils/storageModule";
import { validateArea, validateStoragePlace } from "../../../utils/validation/order";
import { validatePlaceLength } from "../../../utils/validation/order";
import { required } from "../../../utils/validation/required";
import { requiredIfNoFieldValue } from "../../../utils/validation/requiredIfNoFieldValue";
import { ColdStorageSection } from "./ColdStorageSection";
import { CustomFieldsBlock } from "./CustomFieldsBlock";
import { LoadingOrderInput } from "./inputs/LoadingOrderInput";
import { PrintedDocsInput } from "./inputs/PrintedDocsInput";
import { ServiceInput } from "./inputs/ServiceInput";
import { TransporterInput } from "./inputs/TransporterInput";
import { TripNumberInput } from "./inputs/TripNumberInput";
import { OrderProductArticlesBlock } from "./OrderProductArticlesBlock";
import {
	DangerTextInputForInternalNotes,
	ETAETDFormGroup,
	ViewDraftOrdersUtilComponent,
	etaEtdPorts,
	useDriverChange,
	useEtaEtdSync,
	useXPartyChange,
} from "./shared";

// import { isExternalNotApproved } from 'utils/checker/entity';

const rowGroupStyle = {
	borderBottom: "2px solid #bdbdbd",
	paddingBottom: 4,
	marginBottom: 4,
};

const allowFieldsEtaTrans = [
	"eta_date",
	"eta_time",
	"eta_driver",
	"eta_driver_id",
	"eta_driver_phone",
	"eta_truck",
	"eta_truck_id",
	"eta_trailer",
	"eta_trailer_id",
];
const allowFieldsEtdTrans = [
	"etd_date",
	"etd_time",
	"etd_driver",
	"etd_driver_id",
	"etd_driver_phone",
	"etd_truck",
	"etd_truck_id",
	"etd_trailer",
	"etd_trailer_id",
];

export const ETAETDBlock = ({
	isAddMode,
	prefix,
	isFieldHidden,
	isColdStorageService,
	handleLabelSourceChange,
	onIsDocForNfsaChange,
	otherFieldsDisabled,
	...props
}) => {
	const { form } = useForm();
	const { values } = useFormState();
	const { data: terminals } = useTerminalsContext();
	const currentTerminal = terminals[values["terminal_id"]];
	const usesStorageModule = isStorageModuleEnabled(currentTerminal);
	const order_storage_place_field = usesStorageModule ? "storage_place_id" : "area_id";
	const isOutgoingPlukk =
		usesStorageModule && values.service === OrderService.intoPlukkStorage && values.etd_date;
	const validateOrderStoragePlace = usesStorageModule ? validateStoragePlace : validateArea;
	const isTotalsLocked = isAddMode
		? isColdStorageService ||
		  (isOutgoingPlukk && !!currentTerminal?.should_create_pallets_from_packing_list_doc)
		: (isColdStorageService || isOutgoingPlukk) && !!values.has_pallets;

	const externalTerminalFilter = { status: ExternalTerminalConnectionStatus.connected };
	const externalTerminalDisabled = prefix === "eta" || !currentTerminal;
	if (currentTerminal) {
		externalTerminalFilter["own_terminal_guid"] = [terminals[values["terminal_id"]].guid];
	}

	// const companyId = values.company_id || props.companyId;
	const handleDriverChange = useDriverChange(prefix);
	const [
		handle3dPartyChangeOnTruck,
		handle3dPartyChangeOnTrailer,
		handleXPartyChangeOnDriver,
		handleXPartyChangeOn4pl,
	] = useXPartyChange(prefix);
	const handleEtaEtdSync = useEtaEtdSync(prefix);
	// const drivers = useSelector(driversSelector);

	const isDisabledForEditing = !!values[`${prefix}_3rd_party`];
	const isIntoPlukkStorage = values.service === OrderService.intoPlukkStorage;
	const isCrossDock = values.service === OrderService.crossDock;

	const dateRequiredValueKeyPrefix = prefix === "eta" ? "etd" : "eta";
	const portKey = etaEtdPorts[prefix];

	// const currentDriver = drivers[values[`${prefix}_driver_id`]];
	// const currentTransportFilter = { is_deleted: false, is_customer: true };
	// if (currentDriver !== undefined) {
	// 	currentTransportFilter.company_id = currentDriver.company_id;
	// };
	const currentTransportFilter = {
		no_range: true,
		is_deleted: false,
		company_id: props.permissions.company_id || 0,
	};
	const currentTransportSort = { field: "name", order: "ASC" };

	const currentDriverFilter = {
		no_range: true,
		is_blocked: false,
		is_deleted: false,
		company_id: props.permissions.company_id || 0,
	};
	const currentDriverSort = { field: "first_name", order: "ASC" };

	const byTerminalFilter = { terminal_id: [values["terminal_id"]] };
	// const isNot3rdPartyRole = UserRoleByCompanyType.customer_and_transporter_roles.indexOf(props.permissions.role) == -1;
	const placePrefix = prefix === "eta" ? "from" : "to";

	const handle3dPartyChange = (e) => {
		handle3dPartyChangeOnTruck(e);
		handle3dPartyChangeOnTrailer(e);
		handleXPartyChangeOnDriver(e);
		handleXPartyChangeOn4pl(e);
	};

	return (
		<ETAETDFormGroup {...props} prefix={prefix}>
			{/* Row 1: Date, Time, Slot time, Port */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput
						component={DateInput}
						source={`${prefix}_date`}
						validate={[
							isIntoPlukkStorage
								? requiredIfNoFieldValue(`${dateRequiredValueKeyPrefix}_date`)
								: required(),
						]}
						onChange={handleEtaEtdSync}
					/>
				</GridInput>
				<GridInput xs={6} sm={3}>
					<TerminalDatesInput component={TimeInput} source={`${prefix}_time`} />
				</GridInput>
				<GridInput xs={6} sm={3} component={TimeInput} source={`${prefix}_slot_time`} disabled />
				<GridInput
					xs={6}
					sm={3}
					component={PortInput}
					source={portKey}
					hidden={isAddMode}
					filter={byTerminalFilter}
					disabled
				/>
			</FormGroupGrid>

			{/* Row 2: Transporter, Place, Seal, Seal number, Trip number, Loading order */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={6}
					sm={6}
					md={4}
					lg={4}
					component={TransporterInput}
					source={`${prefix}_3rd_party`}
					prefix={prefix}
					excludeIds={[props.permissions.company_id]}
					onChange={handle3dPartyChange}
					hidden={isFieldHidden(`${prefix}_3rd_party`)}
				/>

				<GridInput
					xs={6}
					sm={6}
					md={4}
					lg={4}
					isDefaultTextMode
					component={SelectTextInput}
					sourceText={`place_${placePrefix}`}
					sourceSelect={`${prefix}_external_terminal_id`}
					reference="external-terminal-links"
					optionText="external_name"
					filter={externalTerminalFilter}
					sort={{ field: "external_name", order: "ASC" }}
					validate={[validatePlaceLength]}
					disabledSelect={externalTerminalDisabled}
					allowEmpty
					hidden={isFieldHidden(`place_${placePrefix}`)}
				/>
				<GridInput
					xs={6}
					sm={4}
					md={4}
					lg={4}
					component={CountryISOCodeInput}
					source={`place_${placePrefix}_country_code`}
					hidden={isFieldHidden(`place_${placePrefix}_country_code`)}
				/>
				<GridInput
					xs={6}
					sm={4}
					md={3}
					lg={2}
					component={YesNoInput}
					source={prefix === "eta" ? "seal" : "plumb"}
					defaultValue={false}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL : HideableOrderField.PLUMB)}
				/>
				<GridInput
					xs={6}
					sm={4}
					md={3}
					lg={4}
					component={TextInput}
					source={prefix === "eta" ? "seal_number" : "plumb_number"}
					resettable
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.SEAL_NUMBER : HideableOrderField.PLUMB_NUMBER)}
				/>

				<GridInput
					xs={6}
					sm={6}
					md={3}
					lg={3}
					component={TripNumberInput}
					source={prefix === "eta" ? "incoming_trip_number" : "outgoing_trip_number"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.INCOMING_TRIP_NUMBER : HideableOrderField.OUTGOING_TRIP_NUMBER)}
				/>
				<GridInput
					xs={12}
					sm={6}
					md={3}
					lg={3}
					component={LoadingOrderInput}
					source={prefix === "eta" ? "unloading_sequence" : "loading_sequence"}
					hidden={isFieldHidden(prefix === "eta" ? HideableOrderField.UNLOADING_SEQUENCE : HideableOrderField.LOADING_SEQUENCE)}
				/>
			</FormGroupGrid>

			{/* Row 3: Truck, Trailer, Driver, Phone, Show doc, NFSA doc */}
			<FormGroupGrid style={rowGroupStyle}>
				<GridInput
					xs={12}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					sourceText={`${prefix}_truck`}
					sourceSelect={`${prefix}_truck_id`}
					label={"resources.orders.fields." + prefix + "_truck"}
					reference="trucks"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					textProps={{ inputProps: { style: { textTransform: "uppercase" } } }}
					hidden={isFieldHidden(`${prefix}_truck`)}
				/>
				<GridInput
					xs={12}
					md={prefix === "eta" ? 6 : 4}
					lg={prefix === "eta" ? 3 : 2}
					component={SelectTextInput}
					sourceText={`${prefix}_trailer`}
					sourceSelect={`${prefix}_trailer_id`}
					label={"resources.orders.fields." + prefix + "_trailer"}
					reference="trailers"
					source3rdParty={`${prefix}_3rd_party`}
					filter={currentTransportFilter}
					sort={currentTransportSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					textProps={{ inputProps: { style: { textTransform: "uppercase" } } }}
					hidden={isFieldHidden(`${prefix}_trailer`)}
				/>
				<GridInput
					xs={12}
					md={4}
					lg={2}
					component={SelectTextInput}
					sourceText={`${prefix}_driver`}
					sourceSelect={`${prefix}_driver_id`}
					reference="drivers"
					optionText={(record) =>
						`${record.first_name} ${record.last_name} | ${formatPhoneNumber(record.phone)}`
					}
					filter={currentDriverFilter}
					sort={currentDriverSort}
					disabled={isDisabledForEditing}
					validate={[maxLength(64)]}
					onChange={handleDriverChange}
					hidden={isFieldHidden(`${prefix}_driver`)}
				/>
				<GridInput
					xs={12}
					md={4}
					lg={2}
					component={PhoneInput}
					source={`${prefix}_driver_phone`}
					disabled={isDisabledForEditing}
					hidden={isFieldHidden(`${prefix}_driver_phone`)}
				/>
				{isFieldHidden(`${prefix}_show_doc_for_trans`) ? (
					<HiddenInput source={`${prefix}_show_doc_for_trans`} defaultValue={true} />
				) : (
					<GridInput
						xs={6}
						sm={6}
						md={4}
						lg={2}
						component={YesNoInput}
						source={`${prefix}_show_doc_for_trans`}
						label={"resources.orders.fields." + prefix + "_show_doc"}
						defaultValue={true}
						disabled={!isDisabledForEditing}
					/>
				)}
				{prefix === "etd" && (
					<GridInput
						xs={6}
						sm={6}
						md={4}
						lg={2}
						component={YesNoInput}
						source="is_doc_for_nfsa"
						defaultValue={false}
						onChange={onIsDocForNfsaChange}
						hidden={isFieldHidden(HideableOrderField.IS_DOC_FOR_NFSA)}
					/>
				)}
			</FormGroupGrid>

			{/* Row 4: Goods & Handling (ETA) or Storage allocation (ETD) + Assignee */}
			<FormGroupGrid>
				{prefix === "eta" ? (
					<>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							hidden={
								values.service === OrderService.reloadCarCar ||
								isFieldHidden(HideableOrderField.COOLER_TYPE_ID)
							}
							disabled={otherFieldsDisabled}
						>
							<CoolerTypeSelect filter={{ order_service: values.service }} uppercase />
						</GridInput>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="pallets"
							min={0}
							max={100}
							step={10}
							disabled={otherFieldsDisabled || isTotalsLocked}
							hidden={isFieldHidden(HideableOrderField.PALLETS)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							source="boxes"
							min={0}
							max={100}
							step={5}
							disabled={otherFieldsDisabled || isTotalsLocked}
							hidden={isFieldHidden(HideableOrderField.BOXES)}
						/>
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={NumberInputEx}
							type="float"
							digitsCount={2}
							source="kilos"
							min={0}
							max={1000}
							step={100}
							disabled={otherFieldsDisabled || isTotalsLocked}
							hidden={isFieldHidden(HideableOrderField.KILOS)}
						/>
						<GridInput
							xs={6}
							sm={4}
							md={3}
							lg={2}
							component={CommodityInput}
							source="commodity_id"
							disabled={otherFieldsDisabled}
							hidden={isFieldHidden(HideableOrderField.COMMODITY_ID)}
						/>
						{!isColdStorageService && (
							<GridInput
								xs={6}
								sm={4}
								md={3}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_1"
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
							/>
						)}
					</>
				) : (
					<>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={6}
									sm={4}
									md={2}
									lg={2}
									disabled
								>
									<PlaceSelectInput
										source={order_storage_place_field}
										label={`resources.orders.fields.${order_storage_place_field}`}
										terminalId={values["terminal_id"]}
										validate={validateOrderStoragePlace}
									/>
								</GridInput>
								{!usesStorageModule ? (
									<GridInput
										xs={4}
										sm={3}
										md={2}
										lg={2}
										component={YesNoInput}
										source="is_area_blocked"
										disabled
										hidden={isFieldHidden(HideableOrderField.IS_AREA_BLOCKED)}
									/>
								) : null}
							</>
						)}
						<GridInput
							xs={4}
							sm={3}
							md={2}
							lg={2}
							component={PrintedDocsInput}
							source="printed_doc"
							defaultValue={false}
							disabled={otherFieldsDisabled}
							hidden={isFieldHidden(HideableOrderField.PRINTED_DOC)}
						/>
						{(isIntoPlukkStorage || isCrossDock) && (
							<>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={SelectEnumInput}
									onChange={handleLabelSourceChange}
									options={{ format: "" }}
									source="label_source"
									key={"label_source"}
									enumName="label_source"
									enumObj={LabelSource}
									hidden={isFieldHidden(HideableOrderField.LABEL_SOURCE)}
								/>
								<GridInput
									xs={4}
									sm={3}
									md={2}
									lg={2}
									component={NumberInputEx}
									options={{ format: "" }}
									source="label"
									disabled={!values["label_source"]}
									hidden={isFieldHidden(HideableOrderField.LABEL)}
								/>
							</>
						)}
						<GridInput
							xs={6}
							sm={4}
							md={2}
							lg={2}
							component={OSFCUserSelectInput}
							source="assignee_2"
							hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
						/>
						{etdThirdAssigneeServices.includes(values.service) && (
							<GridInput
								xs={6}
								sm={4}
								md={2}
								lg={2}
								component={OSFCUserSelectInput}
								source="assignee_3"
								hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
							/>
						)}
					</>
				)}
			</FormGroupGrid>

			{/* Row 5: All assignees for Cold Storage (ETA only) */}
			{isColdStorageService && prefix === "eta" && (
				<FormGroupGrid>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_1"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_1)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_2"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_2)}
					/>
					<GridInput
						xs={6}
						sm={4}
						md={4}
						lg={4}
						component={OSFCUserSelectInput}
						source="assignee_3"
						hidden={isFieldHidden(HideableOrderField.ASSIGNEE_3)}
					/>
				</FormGroupGrid>
			)}
		</ETAETDFormGroup>
	);
};

export const FormCustomer = ({ isAddMode, ...props }) => {
	const form = useForm();
	const { permissions } = usePermissions();
	const { values } = useFormState();
	const dataProvider = useDataProvider();
	const translate = useTranslate();
	const [customFields, setCustomFields] = useState([]);
	const { isFieldHidden } = useTerminalFieldVisibility(values?.terminal_id);
	const isColdStorageService = values.service === OrderService.coldStorage;

	React.useEffect(() => {
		if (isAddMode && permissions && permissions.company_id) {
			dataProvider.getOne("customer-companies", { id: permissions.company_id }).then((data) => {
				form.change("commodity_id", data.data.commodity_id);
				// form.change("area_id", data.data.area_id);
				form.change("plumb", data.data.plumb);
				form.change("seal", data.data.seal);
				form.change("terminal_id", data.data.terminal_id);
				form.change("is_copy_documents", data.data.copy_docs_on_clone);
				// form.change("set_order_completed", data.data.set_order_completed);
			});
			dataProvider
				.getList("custom-fields", {
					pagination: { page: 0, perPage: -1 },
					sort: { field: "id", order: "ASC" },
					filter: { company_id: permissions.company_id },
				})
				.then((data) => {
					if (data?.data) {
						const processedData = data.data.map((item) => {
							return {
								id: null,
								field_id: item.id,
								name: item.name,
								type: item.type,
								value: null,
								key: item.key,
							};
						});
						setCustomFields(processedData);
						form.change("custom_fields", processedData);
					}
				});
		} else {
			setCustomFields(props.record.custom_fields);
		}
	}, [isAddMode, permissions, props.record.custom_fields]);

	if (!permissions) return null;

	// const statusDisabled = OrderStatusCustomerEdit.indexOf(props.record.status) === -1;
	const isTerminalDisabled = values["status"]
		? !OrderStatusEditOrderTerminal.includes(values["status"])
		: false;

	let statusDisabled;
	let enabledFields;
	let otherFieldsDisabled = false;

	if (!isAddMode) {
		const status = props.record.status;
		if (props.record.deleted_at) {
			enabledFields = {};
		} else if (OrderStatusCustomerEditOrder.indexOf(status) !== -1) {
			// otherFieldsDisabled = true;
			statusDisabled = OrderStatusCustomerEdit.indexOf(status) === -1;
		} else if (OrderStatusTransporterEditOrder_2.indexOf(status) !== -1) {
			enabledFields = {
				notes: true,
				internal_notes: true,
			};
			const eta_3rd_party = props.record.eta_3rd_party;
			if (!eta_3rd_party) {
				for (const key of allowFieldsEtaTrans) {
					enabledFields[key] = true;
				}
			}
			const etd_3rd_party = props.record.etd_3rd_party;
			if (!etd_3rd_party) {
				for (const key of allowFieldsEtdTrans) {
					enabledFields[key] = true;
				}
			}
			if (OrderStatusTransporterEditOrder_1.indexOf(status) !== -1) {
				enabledFields["loading_sequence"] = true;
			}
		} else if (status === OrderStatus.ORDER_COMPLETED || status === OrderStatus.DELETED) {
			enabledFields = {};
		} else {
			enabledFields = { notes: true, internal_notes: true };
		}
	}

	// if (isExternalNotApproved(props.record.state)) {
	// 	enabledFields = {};
	// }

	const handleLabelSourceChange = (event) => {
		const val = event.target.value;
		if (val == LabelSource.pallets) {
			form.change("label", values.pallets);
		} else if (val == LabelSource.boxes) {
			form.change("label", values.boxes);
		}
	};

	const onTerminalChange = (val) => {
		form.change("port_in", null);
		form.change("port_out", null);
		form.change("storage_place_id", null);
		form.change("area_id", null);
		form.change("is_area_blocked", false);
		form.change("is_per", false);
	};

	const isStatusDraft = values.status === OrderStatus.DRAFT;
	const isOrderDraft = props?.record ? props.record.status === OrderStatus.DRAFT : isStatusDraft;
	const statusAllowedIds = isOrderDraft
		? OrderStatusAllowedFromDraft
		: OrderStatusCustomerEdit.filter((s) => s !== OrderStatus.DRAFT);
	const isOrderProductArticleVisible = isColdStorageService && !isStatusDraft && !isAddMode;
	return (
		<React.Fragment>
			<ViewDraftOrdersUtilComponent {...props} />
			<GridForm {...props} enabledFields={enabledFields}>
				<HiddenInput source="isUpdatePartial" defaultValue={1} />
				<FormGroupGrid>
					<GridInput
						source="reference"
						sm={3}
						component={OrderReferenceInput}
						validate={!isStatusDraft ? [required()] : undefined}
						autoFocus
					/>
					<GridInput sm={1}>
						<ReferenceInput
							source="terminal_id"
							reference="terminal"
							disabled={isTerminalDisabled}
							validate={[required()]}
							onChange={onTerminalChange}
						>
							<SelectInput optionText="name" />
						</ReferenceInput>
					</GridInput>
					<GridInput
						source="service"
						sm={3}
						component={ServiceInput}
						disabled={!isAddMode && isColdStorageService}
						validate={!isStatusDraft ? [required()] : undefined}
						allowEmpty={isStatusDraft}
						defaultValue={!isStatusDraft ? OrderService.reloadCarCar : undefined}
						isCouldStorageVisible={isAddMode || isColdStorageService}
					/>
					<GridInput
						sm={1}
						component={YesNoInput}
						source="is_per"
						defaultValue={false}
						hidden={
							isFieldHidden(HideableOrderField.IS_PER) ||
							!perEligibleServices.includes(values.service)
						}
					/>
					<GridInput
						sm={3}
						hidden={isAddMode}
						disabled={statusDisabled}
						validate={statusDisabled ? undefined : [required()]}
					>
						<ReferenceInput source="status" reference="order-statuses">
							<SelectDisabledInput allowedIds={statusAllowedIds} optionText="name" />
						</ReferenceInput>
					</GridInput>
					<GridInput sm={1} component={YesNoInput} source="priority" defaultValue={false} />
					{/* <GridInput sm={1} component={YesNoInput} source="set_order_completed" defaultValue={false} /> */}
					{/* <GridInput sm={2} component={LoadNoInput} source="load_no" /> */}
				</FormGroupGrid>

				{/* <Grid item xs={12} md={10} lg={6}> */}
				<ETAETDBlock
					xs={12}
					md={10}
					lg={6}
					prefix="eta"
					permissions={permissions}
					isAddMode={isAddMode}
					{...props}
					enabledFields={enabledFields}
					isFieldHidden={isFieldHidden}
					isColdStorageService={isColdStorageService}
					otherFieldsDisabled={otherFieldsDisabled}
				/>
				{/* </Grid>
			<Grid item xs={12} md={10} lg={6}> */}
				{!isColdStorageService ? (
					<ETAETDBlock
						xs={12}
						md={10}
						lg={6}
						prefix="etd"
						permissions={permissions}
						isAddMode={isAddMode}
						{...props}
						enabledFields={enabledFields}
						isFieldHidden={isFieldHidden}
						isColdStorageService={isColdStorageService}
						handleLabelSourceChange={handleLabelSourceChange}
						otherFieldsDisabled={otherFieldsDisabled}
					/>
				) : (
					<ColdStorageSection sm={12} md={12} lg={6} isAddMode={isAddMode} {...props} />
				)}
				{/* </Grid> */}

				{isOrderProductArticleVisible && !isOrderDraft && (
					<OrderProductArticlesBlock
						orderId={props.record.id}
						pallets={props.record.pallets}
						companyId={props.record.company_id}
						status={props.record.status}
						totalBoxes={props.record.total_boxes}
					/>
				)}
				{/* <DimensionsBlock {...props} /> */}
				<FormGroupGrid>
					<GridInput
						sm={12}
						md={6}
						component={TextInput}
						source="notes"
						multiline
						rows={6}
						validate={[maxLength(1024)]}
					/>
					<GridInput
						sm={12}
						md={6}
						className="red-border"
						component={DangerTextInputForInternalNotes}
						source="internal_notes"
						multiline
						rows={6}
						validate={[maxLength(1000)]}
					/>
				</FormGroupGrid>
				<CustomFieldsBlock customFields={customFields} enabledFields={enabledFields} />
			</GridForm>
		</React.Fragment>
	);
};
